import { ConnectionPositionPair } from '@angular/cdk/overlay'
import { ChangeDetectionStrategy, Component, inject, Input, ViewEncapsulation } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { AccountFacade, SocialLoginRegisterTypeEnum } from '@galabid/gb-dashboard/account/data-access'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import * as AccountActions from 'libs/gb-dashboard/account/data-access/src/lib/+state/account.actions'
import { AuctionsConstants } from 'libs/gb-dashboard/auctions/feature/src/lib/auctions.constants'
import { GeolocationFacade } from 'libs/gb-dashboard/shared/data-access-geolocation/src/lib/+state/geolocation.facade'
import {
  GB_LAST_LOGIN_DATE,
  GbLocalStorageService,
} from 'libs/gb-dashboard/shared/utils/src/lib/gb-localstorage-service/gb-localstorage.service'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'

const POPOVER_POSITIONS: ConnectionPositionPair[] = [
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -10,
  },
]

@Component({
  selector: 'gb-social-login-register',
  templateUrl: './gb-social-login-register.component.html',
  styleUrls: ['./gb-social-login-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GbSocialLoginRegisterComponent {
  private readonly accountFacade = inject(AccountFacade)
  private readonly geolocationFacade = inject(GeolocationFacade)
  private readonly gbLocalStorageService = inject(GbLocalStorageService)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly router = inject(Router)
  protected readonly appConfig = inject<EnvironmentModel>(APP_CONFIG)

  @Input() type: 'login' | 'register' = 'login'

  protected readonly providers = SocialLoginRegisterTypeEnum
  protected readonly popoverPositions = POPOVER_POSITIONS
  protected showNonSaasGooglePopover = false

  private readonly lastLogin = this.gbLocalStorageService.getRawItem(GB_LAST_LOGIN_DATE)
  private readonly isSaasLocation = toSignal(this.geolocationFacade.isSaasLocation$, { initialValue: null })

  private readonly registerLabels = {
    [SocialLoginRegisterTypeEnum.Facebook]: 'REGISTER.SSO.FACEBOOK',
    [SocialLoginRegisterTypeEnum.Google]: 'REGISTER.SSO.GOOGLE',
    [SocialLoginRegisterTypeEnum.Microsoft]: 'REGISTER.SSO.MICROSOFT',
    [SocialLoginRegisterTypeEnum.Apple]: 'REGISTER.SSO.APPLE',
  }

  private readonly loginLabels = {
    [SocialLoginRegisterTypeEnum.Facebook]: 'AUTH.LOGIN.SSO.FACEBOOK',
    [SocialLoginRegisterTypeEnum.Google]: 'AUTH.LOGIN.SSO.GOOGLE',
    [SocialLoginRegisterTypeEnum.Microsoft]: 'AUTH.LOGIN.SSO.MICROSOFT',
    [SocialLoginRegisterTypeEnum.Apple]: 'AUTH.LOGIN.SSO.APPLE',
  }

  getLabel(provider: SocialLoginRegisterTypeEnum): string {
    return this.type === 'login' ? this.loginLabels[provider] : this.registerLabels[provider]
  }

  onClick(provider: SocialLoginRegisterTypeEnum): void {
    if (provider === this.providers.Google && !this.isSaasLocation() && !this.lastLogin) {
      this.showNonSaasGooglePopover = !this.showNonSaasGooglePopover
      return
    }

    this.type === 'login' ? this.socialLogin(provider) : this.socialRegister(provider)
  }

  onContactClick(): void {
    this.showNonSaasGooglePopover = false
    const newWindow = window.open(this.appConfig.gbContactUrl, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  socialRegister(provider: SocialLoginRegisterTypeEnum): void {
    this.accountFacade.dispatch(AccountActions.socialRegister({ provider }))
  }

  socialLogin(provider: SocialLoginRegisterTypeEnum): void {
    this.accountFacade.dispatch(AccountActions.socialLogin({ provider, onSuccess: () => this.redirectUrl() }))
  }

  redirectUrl(): void {
    const redirectUrl = this.activatedRoute.snapshot.queryParamMap.get('redirectUrl')

    if (redirectUrl) {
      this.router.navigate([redirectUrl])
    } else {
      this.router.navigate([AuctionsConstants.AUCTION])
    }
  }
}
